import { useEffect, useState } from 'react'
import { Alert, Button, Modal, Spin } from 'antd'
import { useAuthInfo } from '../auth/hooks'
import * as api from '../lib/api'
import './ConsentGate.css'

function filterConsentDocument (html, status) {
  const document = new window.DOMParser().parseFromString(html, 'text/html')
  const regionCodes = new Set(status.regions.map(region => region.code))

  document.querySelectorAll('[data-consent-regions]').forEach(statement => {
    const applicableRegions = statement.dataset.consentRegions.split(' ')
    const applies = applicableRegions.includes('all') || applicableRegions.some(region => regionCodes.has(region))
    if (!applies) statement.remove()
  })

  if (status.mode !== 'accept_decline') {
    document.querySelectorAll('[data-explicit-consent-only]').forEach(statement => statement.remove())
  }

  return document.body.innerHTML
}

export default function ConsentGate ({ children }) {
  const { isAuthenticated, requiresReauthentication, pendingFlow, user } = useAuthInfo()
  const authenticationComplete = isAuthenticated && !requiresReauthentication && !pendingFlow && Boolean(user?.id)
  const [consent, setConsent] = useState(null)
  const [documentHtml, setDocumentHtml] = useState('')
  const [error, setError] = useState('')
  const [statusError, setStatusError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [declineRecorded, setDeclineRecorded] = useState(false)
  const [openForUserId, setOpenForUserId] = useState(null)
  const consentOpen = authenticationComplete && openForUserId === user?.id && consent?.required === true

  useEffect(() => {
    let active = true
    let consentRequired = false
    setConsent(null)
    setDocumentHtml('')
    setError('')
    setStatusError('')
    setDeclineRecorded(false)
    setOpenForUserId(null)
    if (!authenticationComplete) return () => { active = false }

    api.getConsentStatus()
      .then(async status => {
        if (!active) return
        setConsent(status)
        if (!status.required) return
        consentRequired = true
        setOpenForUserId(user.id)
        const documents = await Promise.all(status.documents.map(async document => {
          const html = await api.getConsentDocument(document)
          const filteredHtml = filterConsentDocument(html, status)
          return status.documents.length > 1
            ? `<h1>${document.title}</h1>${filteredHtml}`
            : filteredHtml
        }))
        if (active) setDocumentHtml(documents.join('<hr>'))
      })
      .catch(err => {
        if (!active) return
        const message = err.message || 'Unable to load the privacy notice.'
        if (consentRequired) setError(message)
        else setStatusError(message)
      })
    return () => { active = false }
  }, [authenticationComplete, user?.id])

  if (!authenticationComplete) return children
  if (statusError) {
    return (
      <div className='consent-gate-background'>
        <Alert type='error' showIcon title={statusError} className='consent-gate-alert' />
      </div>
    )
  }
  if (!consentOpen) return children

  const respond = async decision => {
    setSubmitting(true)
    setError('')
    try {
      const status = await api.respondToConsent(decision)
      setConsent(status)
      setOpenForUserId(status.required ? user.id : null)
      if (decision === 'declined' && status.required) setDeclineRecorded(true)
    } catch (err) {
      setError(err.message || 'Unable to record your response.')
    } finally {
      setSubmitting(false)
    }
  }

  const explicit = consent?.mode === 'accept_decline'

  return (
    <div className='consent-gate-background'>
      <Modal
        open={consentOpen}
        centered
        width={980}
        closable={false}
        keyboard={false}
        mask={{ closable: false }}
        title={null}
        footer={null}
        styles={{ body: { padding: 0 } }}
      >
        <div className={`consent-gate${declineRecorded ? ' consent-gate--confirmation' : ''}`}>
          {error && <Alert type='error' showIcon title={error} className='consent-gate-alert' />}

          {declineRecorded
            ? (
              <section className='consent-decline-confirmation' aria-live='polite'>
                <Alert type='success' showIcon title='Your consent decision has been recorded.' />
              </section>
              )
            : (
              <>
                <section className='consent-document' aria-label='Privacy notice'>
                  {!consent || (!documentHtml && !error)
                    ? <div className='consent-loading'><Spin size='large' /></div>
                    : <div dangerouslySetInnerHTML={{ __html: documentHtml }} />}
                </section>

                <footer className='consent-gate-actions'>
                  <div className='consent-gate-buttons'>
                    {explicit && (
                      <Button danger size='large' disabled={!documentHtml} loading={submitting} onClick={() => respond('declined')}>
                        Decline
                      </Button>
                    )}
                    <Button type='primary' size='large' disabled={!documentHtml} loading={submitting} onClick={() => respond(explicit ? 'accepted' : 'proceeded')}>
                      {explicit ? 'Accept' : 'Proceed'}
                    </Button>
                  </div>
                </footer>
              </>
              )}
        </div>
      </Modal>
    </div>
  )
}
